import { PiFire, PiClock, PiCalendarBlank } from 'react-icons/pi'
import type { IconType } from 'react-icons'
import type {
  EnrichedAction,
  SignalType,
} from '../../services/recommendations_api'
import { SIGNAL_CONFIG } from './recommendationConfig'

/**
 * Figma badge styling for each urgency level. Maps to semantic status
 * tokens (`error`/`warning`/`info`) so theme/dark-mode changes
 * propagate cleanly.
 */
export const URGENCY_BADGE_CLASSES: Record<EnrichedAction['urgency'], string> =
  {
    do_now: 'bg-error-bg-subtle text-error-strong',
    today: 'bg-warning-bg text-warning-strong',
    this_week: 'bg-info-bg-subtle text-info-strong',
  }

/**
 * Icon for each signal type. Derived from SIGNAL_CONFIG.
 */
export const SIGNAL_TYPE_ICONS: Record<SignalType, IconType> =
  Object.fromEntries(
    Object.entries(SIGNAL_CONFIG).map(([k, v]) => [k, v.icon])
  ) as Record<SignalType, IconType>

/**
 * Configuration for urgency level icons.
 */
export const URGENCY_ICONS = {
  do_now: PiFire,
  today: PiClock,
  this_week: PiCalendarBlank,
} as const

/**
 * Tooltip content for urgency levels.
 */
export const URGENCY_TOOLTIPS: Record<
  EnrichedAction['urgency'],
  { title: string; content: string }
> = {
  do_now: {
    title: 'Hot',
    content: 'Your highest-priority actions right now',
  },
  today: {
    title: 'Today',
    content: 'Not immediately urgent but still time-sensitive',
  },
  this_week: {
    title: 'This Week',
    content: 'Plan to complete within the next few days',
  },
}

/**
 * Number of items to show by default before requiring "Show More" button.
 */
export const DEFAULT_VISIBLE_ITEMS = 3

/**
 * Icon colors for each signal type.
 */
export const SIGNAL_ICON_COLORS: Record<SignalType, string> =
  Object.fromEntries(
    Object.keys(SIGNAL_CONFIG).map((k) => [k, 'text-fg-secondary'])
  ) as Record<SignalType, string>

/**
 * Format a signal-specific label showing the days metric.
 * Delegates to the signal config's formatLabel function.
 */
export const formatSignalLabel = (
  signalType: SignalType,
  daysUnreplied: number | null,
  daysStale: number | null,
  daysInStage: number | null,
  currentStage: string | null,
  daysSinceEmail: number | null = null,
  daysOverdue: number | null = null
): string => {
  const config = SIGNAL_CONFIG[signalType]
  if (!config) return ''
  // Build a minimal EnrichedAction-like object for the formatter
  return config.formatLabel({
    days_unreplied: daysUnreplied,
    days_stale: daysStale,
    days_in_stage: daysInStage,
    days_since_email: daysSinceEmail,
    days_overdue: daysOverdue,
    current_stage: currentStage,
  } as EnrichedAction)
}
